// packages/web/src/components/account/invite-panel.tsx
/**
 * InvitePanel — invite code display + copy + usage quota.
 * 19BN Phase 4: fetches /account/invite on mount.
 */

import { useEffect, useState } from 'react';
import { useT } from '../../i18n/use-t.js';
import { inviteApi } from '../../api/invite.js';

interface InvitePanelData {
  inviteCode: string;
  quotaUsed: number;
  quotaTotal: number;
  records: Array<{
    id: string;
    invitee_email: string;
    status: string;
    created_at: number;
  }>;
}

export function InvitePanel() {
  const t = useT();
  const [data, setData] = useState<InvitePanelData | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void (async () => {
      // 19BN Phase D D2: fetch via the unified client. 401 → null (anonymous
      // user, panel shows its loading fallback). Other errors are swallowed
      // (invite panel is non-critical; user can reopen the tab to retry).
      try {
        const result = await inviteApi.get();
        if (result) setData(result);
      } catch { /* non-critical */ }
    })();
  }, []);

  function copyCode() {
    if (!data?.inviteCode) return;
    navigator.clipboard?.writeText(data.inviteCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }).catch(() => undefined);
  }

  if (!data) return <div style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 'var(--space-3xl)' }}>加载中...</div>;

  return (
    <div>
      <div style={{ background: 'linear-gradient(135deg, var(--color-primary), var(--color-primary-hover))', color: 'white', borderRadius: 'var(--radius-lg)', padding: 'var(--space-2xl)', marginBottom: 'var(--space-xl)' }}>
        <div style={{ fontSize: 'var(--text-sm)', opacity: 0.8, marginBottom: 'var(--space-sm)' }}>{t('auth.inviteCode')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-lg)' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-2xl)', fontWeight: 700, letterSpacing: '2px', flex: 1 }}>
            {data.inviteCode}
          </span>
          <button
            onClick={copyCode}
            style={{ padding: 'var(--space-sm) var(--space-lg)', background: copied ? 'var(--color-success)' : 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 'var(--radius-sm)', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: 'var(--text-sm)' }}
          >
            {copied ? '✓ 已复制' : '复制'}
          </button>
        </div>
        <div style={{ fontSize: 'var(--text-sm)', opacity: 0.75, marginTop: 'var(--space-lg)' }}>
          邀请 {data.quotaUsed}/{data.quotaTotal} · 双方各得 500 点
        </div>
      </div>
      {/* Progress bar */}
      <div style={{ height: '6px', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-pill)', overflow: 'hidden', marginBottom: 'var(--space-xl)' }}>
        <div style={{ height: '100%', width: `${(data.quotaUsed / data.quotaTotal) * 100}%`, background: 'linear-gradient(90deg, var(--color-action), var(--color-gold))', borderRadius: 'var(--radius-pill)' }} />
      </div>
      {/* Records */}
      {data.records.length > 0 && (
        <div>
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 'var(--space-lg)' }}>邀请记录</div>
          {data.records.map((r) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-lg)', padding: 'var(--space-lg) 0', borderBottom: '1px solid var(--color-border)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, color: 'var(--color-ink)' }}>{r.invitee_email}</div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{new Date(r.created_at).toLocaleDateString()}</div>
              </div>
              <span style={{ padding: '2px 10px', borderRadius: 'var(--radius-pill)', fontSize: 'var(--text-xs)', fontWeight: 600, background: r.status === 'rewarded' ? 'var(--color-success-soft)' : 'var(--color-warning-soft)', color: r.status === 'rewarded' ? 'var(--color-success-deep)' : 'var(--color-amber-deep)' }}>
                {r.status === 'rewarded' ? '已奖励' : '待奖励'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
